import { FC, useEffect, useMemo, useState } from 'react';
import { Search } from '@material-ui/icons/';
import { Box, IconButton, Spinner } from '@chakra-ui/react';

import { 
  sortEntityByName,
  normalizeBic,
  ValidateError, 
  validateInn
} from '@app/utils';
import { useEntityState } from '@app/hooks/use-entity-state';
import AutocompleteField from '@app/components/common/autocomplete-field';
import SelectField from '@app/components/common/select-field';
import TextField from '@app/components/common/text-field';
import { useGuide } from '@app/hooks/use-guide';
import { useAutocomplete, usePaginatedAutocomplete } from '@app/hooks/use-autocomplete';
import FieldView from '@app/components/common/field-view';
import { useAlert } from '@app/hooks/use-alert';
import { ImageDescriptor } from '@app/api/models/image';
import { Bank } from '@app/api/models/outlet';
import { Form } from '@app/components/common/form/form';
import { Txt } from '@app/components/common';
import { FieldComponentProps } from '@app/components/common/types';
import { VisitFrequencySelectField } from '@app/components/common/visit-frequency';
import { OutletField } from '@app/pages/equipments/dialogs/components/OutletField';

import { validateForm } from './utils/validation';
import { Channel, DDOutletData, NewOutletFormInstance } from './types';
import { RouteNumberField } from '../outlets/components/route-number-field';
import { PaymentDefermentField } from '../outlets/components/payment-deferment-field';
import { CorrAccountField } from '../outlets/components/corr-account-field';
import { ContactFields } from '../outlets/components/contact-fields';

interface OutletFormProps {
  instance: Partial<NewOutletFormInstance>;
  submitText?: string;
  onSubmit: (instance: Partial<NewOutletFormInstance>) => void;
  onCancel: () => void;
  view?: boolean;
}

const NewOutletForm: FC<OutletFormProps> = (props) => {
  const { warning, error } = useAlert();
  const {
    instance,
    submitText,
    onSubmit,
    onCancel,
    view,
  } = props;

  const [outlet, onChange, setInstance] =
    useEntityState<NewOutletFormInstance>(instance);
  const [loading, setLoading] = useState(false);

  const [images, setImages] = useState<ImageDescriptor[]>([]);

  const [
    {
      divisions,
      paymentTypes,
      priceLists,
      channels,
      subChannels,
    },
    setGuides,
  ] = useState<Record<string, EntityBase[]>>({
    divisions: [],
    paymentTypes: [],
    priceLists: [],
    channels: [],
    subChannels: [],
    rawChannels: [],
  });

  const getDivision = useGuide('division', undefined, setLoading);

  const getChannels = useGuide('outlet_sub_channel', undefined, setLoading);
  const getPaymentTypes = useGuide('payment_type', undefined, setLoading);
  const getPriceLists = useGuide('price_list', undefined, setLoading);
  const getAutocomplete = useAutocomplete();
  const getPaginatedAutocomplete = usePaginatedAutocomplete();

  const corrAccounts = useMemo(() => {
    return (
      outlet.bank?.accounts?.map((account) => ({
        id: account,
        name: '' + account,
      })) || []
    );
  }, [outlet.bank]);

  useEffect(() => {
    (async () => {
      const channels = (await getChannels()) as Channel[];

      const guides = {
        divisions: await getDivision(),
        paymentTypes: await getPaymentTypes(),
        priceLists: await getPriceLists(),
        rawChannels: channels,
        channels: sortEntityByName(
          channels.reduce<EntityBase[]>((acc, item, index) => {
            if (acc.find(({ name }) => name === item.channel)) {
              return acc;
            }
            return [...acc, { id: index, name: item.channel }];
          }, [])
        ),
        subChannels: [],
      };

      setGuides(guides);

      setLoading(false);
    })();
  }, []);

  

  const onSubmitForm = async () => {
    const { isValid, errors } = await validateForm(outlet);
    if (!isValid) {
      warning(Object.values(errors).join('. '));
      return;
    }

    const result = {
      ...outlet,
      photos: images.map((item) => ({
        image_pid: item.image_pid,
        content: item.src,
        filename: '0001.jpg',
      })),
    };

    onSubmit(result);
  };

  const getLegalData = async (inn: string) => {
    const e: ValidateError = { code: 0, message: '' };
    if (!inn || !validateInn(inn, e)) {
      error(e.message);
      return;
    }

    const data: DDOutletData[] = await getAutocomplete('dd/legal', {
      search: inn,
    }) as DDOutletData[];

    if (data.length) {
      return {
        name: data[0].name,
        address: { 
          full_address: data[0].address || '',
          address: data[0].address || '',
          city: '',
          region: '',
        },
        kpp: data[0].kpp,
        ogrn: data[0].ogrn,
        okpo: data[0].okpo,
      };
    } else {
      error(e.message || 'Проверьте ИНН, не удалось получить данные организации по ИНН');
      return;
    }
  };

  if (loading) {
    return <Box
      position="absolute"
      style={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
      top={0}
      left={0}
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Spinner />
    </Box>;
  }
  
  return (<>
    <Form 
      instance={outlet}   
      onSubmit={!view && onSubmitForm}
      submitText={submitText} 
      onCancel={onCancel}    
      onChange={!view && onChange}
    >
      <Form.Item
        label="Предшественник (ТТ)"
        field='parent'
        component={OutletField}
        span={{ md: 5 }}
        readOnly={view}
      />      
      <Form.Divider />
      <Form.Item
        required
        label="Наименование"
        field='name'
        component={TextField}
        span={{ md: 12 }}
        readOnly={view}
      />
      <Form.Item
        label="Подсеть"
        field='subnet'
        component={TextField}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        required
        label="Вывеска"
        field='title'
        component={TextField}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Divider />
      <Form.Item
        required
        label="ИНН"
        field='inn'
        component={TextField}
        componentProps={{
          rightElement: <IconButton
            disabled={view}
            size='small'
            onClick={async () => {              
              !view && setInstance({
                ...outlet,
                ...(await getLegalData(outlet.inn)),
              });
            } } aria-label='search'     
          >
            <Search />
          </IconButton>
        }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        label="ОГРН"
        field='ogrn'
        component={TextField}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        required
        label="Дивизион"
        field='division'
        component={SelectField}
        componentProps={{
          options: divisions,
        }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        required
        label="Дистрибьютор"
        field='distributor'
        component={AutocompleteField}
        componentProps={{
          name: 'distributor',
          valueProperty: 'name',
          onFetchOptions: getAutocomplete,
        }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        required
        label="Канал"
        field='channel'
        component={SelectField}
        componentProps={{
          options: channels,
        }}
        onChange={(value) => {
          onChange('sub_channel')(null);
          setGuides((guides) => ({
            ...guides,
            subChannels: sortEntityByName(
              guides.rawChannels.filter((item) => {
                return (
                  item.channel?.toUpperCase() ===
                  (value?.name || '').toUpperCase()
                );
              })
            ),
          }));
        }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        required
        label="Подканал"
        field='sub_channel'
        component={SelectField}
        componentProps={{
          options: subChannels,
        }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        label="День визита в ТТ"
        field='visits_day'
        required
        component={VisitFrequencySelectField}
        componentProps={{ 
          disabledZeroOption: true,
         }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Item
        label="Номер маршрута"
        field='route_number'
        required
        component={RouteNumberField}
        componentProps={{
          name: 'route_number',
          valueProperty: 'name',
          onFetchOptions: getPaginatedAutocomplete,
        }}
        span={{ md: 6 }}
        readOnly={view}
      />
      <Form.Divider />
      <Form.Item
        required
        label="Фактический адрес"
        field='address'
        component={({ value, ...props }: FieldComponentProps) => <AutocompleteField
          id="address"
          value={value?.full_address}
          {...props}        
        />}
        componentProps={{
          name: 'dd/address',
          valueProperty: 'address',
          onFetchOptions: getAutocomplete,
        }}
        span={{ md: 12 }}
        onChange={(value) => {
          onChange('latlng')(value.latlng);
        }}
        readOnly={view}
      />
      {/* <Form.Spacer span={{ md: 12 }}>
        <Box position="relative" height={200}>
          <SimpleMap
            points={points}
            cursor={isDroppingMapPointer ? 'pointer' : undefined}
            onMapClick={onMapClick}
            onDragOnMap={onDragOnMap}
          />
          <Box
            position="absolute"
            top={3}
            left={3}
            zIndex="1000"
          >
            {isNullCoords(outlet.address?.latlng) && <IconButton onClick={onAddCoordClick} aria-label="add">
              <AddLocation color={isDroppingMapPointer ? 'secondary' : 'primary'} />
            </IconButton>}
          </Box>
        </Box>
      </Form.Spacer> */}
      <Form.Divider />
      <Form.Item
        label="Прайс-лист"
        field='price'
        component={SelectField}
        componentProps={{
          options: priceLists,
        }}
        span={{ md: 4 }}
        readOnly={view}
      />
      <Form.Item
        label="Тип оплаты"
        field='payment_type'
        component={SelectField}
        componentProps={{
          options: paymentTypes,
        }}
        span={{ md: 4 }}
        readOnly={view}
      />
      <Form.Item
        label="Отсрочка платежа"
        field='payment_deferment'
        component={PaymentDefermentField}
        span={{ md: 4 }}
        readOnly={view}
      />
      {outlet.payment_type?.id === 20 && (<>
        <Form.Divider />
        <Form.Item
          label="КПП"
          field='kpp'
          component={TextField}
          span={{ md: 6 }} 
          readOnly={view}
        />
        <Form.Item
          label="ОКПО"
          field='okpo'
          component={TextField}
          span={{ md: 6 }} 
        />
        <Form.Item
          label="БИК банка"
          field='bank'
          component={AutocompleteField}
          componentProps={{
            name: 'bank',
            valueProperty: 'name',
            onFetchOptions: getAutocomplete,
            renderOption: (option: Bank) => (
              <Box display="flex">
                <Box
                  style={{ rowGap: '8px' }}
                  display="flex"
                  flexDirection="column"
                >
                  <Txt color="gray.500">
                    БИК {normalizeBic(option.bic)}&nbsp;
                  </Txt>
                  <Txt>{option.name}</Txt>
                  <FieldView
                    title="Адрес"
                    value={(option as Bank)?.address}
                  />
                </Box>
              </Box>
            )
          }}
          span={{ md: 6 }}
        />
        <Form.Spacer span={{ md: 6 }}>{outlet.bank?.name || 'Введите БИК банка'}</Form.Spacer>
        <Form.Item
          label="Кор.счет"
          field='corr_account'
          component={CorrAccountField}
          componentProps={{
            options: corrAccounts,
          }}
          span={{ md: 6 }}
        />
        <Form.Item
          label="Р/счет"
          field='account'
          component={TextField}
          componentProps={{
            max: 21
          }}
          span={{ md: 6 }} 
        />
      </>)}
      <Form.Divider />
      <Form.Item
        label="Директор - ФИО"
        field='director'
        component={TextField}
        span={{ md: 6 }} 
        readOnly={view}
      />
      <Form.Item
        label="Tелефон"
        field='phone'
        component={TextField}
        span={{ md: 3 }} 
        readOnly={view}
      />
      <Form.Item
        label="Email"
        field='email'
        component={TextField}
        span={{ md: 3 }} 
        readOnly={view}
      />
      <Form.OutletItem
        label="Контактное лицо (1)"
        field='contact_person_1'
        component={ContactFields}
        componentProps={{ instance, field: 'contact_person_1' }}
        readOnly={view}
      />
      <Form.OutletItem
        label="Контактное лицо (2)"
        field='contact_person_2'
        component={ContactFields}
        componentProps={{ origin, instance, field: 'contact_person_2' }}
        readOnly={view}
      />
      <Form.Divider />
      {/* <Form.Spacer span={{ md: 12 }} >
        <ImagesField 
          images={images}
          loading={loading}
          setImages={setImages}
        /> */}
      <Form.Spacer/> 
    </Form>
  </>);
};

export default NewOutletForm;
